import { FullAccountNumber } from "@common/dto/financialAccount/get-account-numbers-response.dto";
import { currencyFormatter } from "@common/helpers/currency";
import { FinancialAccount } from "@common/types/financialAccount";
import { isNumber } from "@common/helpers/typeGuards";
import { keyBy } from "@common/helpers/collections";
import React, {
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import {
    ExtendedFinancialAccount,
    getFinancialAccountNumbers,
} from "@lib/financialAccount";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { HStack } from "@design-system/HStack/HStack";
import { SectionHeader } from "@design-system/Section/Section";
import { useEntity } from "../../entity/useEntity";
import { EntityNoAccounts } from "../../accounts/EntityNoAccounts";
import { AddAccount } from "../../accounts/AddAccount";
import { TablePageHeader } from "../../pageHeader/TablePageHeader";
import { EntitySettingsPages } from "../constants";
import { getEntityPaneKey } from "../lib";
import { useSettings } from "../SettingsProvider";
import { AccountDetails } from "./AccountDetails/AccountDetails";
import "./AccountsSettings.scss";

type AccountNumbers = Record<number, FullAccountNumber>;

async function getAccountNumbers(): Promise<AccountNumbers> {
    const numbers = await getFinancialAccountNumbers();

    return keyBy(numbers.numbers, "id");
}

interface Props {
    scrollbars: RefObject<Scrollbars | null>;
}

export const AccountsSettings: React.FC<Props> = ({ scrollbars }) => {
    const [search, setSearch] = useState("");
    const entity = useEntity();
    const baseAccounts = useFinancialAccounts({
        entityId: entity.id,
    });
    const {
        path: [accountsPage, activeAccount],
    } = useSettings();

    const [activeAccountId, setActiveAccountId] = useState<number | undefined>(
        activeAccount ? parseInt(activeAccount, 10) : undefined,
    );

    const [accountNumbers, setAccountNumbers] = useState<AccountNumbers>();
    const [openedAccounts, setOpenedAccounts] = useState<number[]>([]);

    useEffect(() => {
        getAccountNumbers().then((numbers) => {
            setAccountNumbers(numbers);
        });
    }, []);

    const scrollToAccount = useCallback(
        (accountId: number) => {
            setTimeout(() => {
                const el = document.querySelector<HTMLElement>(
                    `[data-account-id="${accountId}"]`,
                );
                if (el && scrollbars.current) {
                    const elementRect = el.getBoundingClientRect();
                    const scrollbarsRect =
                        scrollbars.current.container.getBoundingClientRect();
                    scrollbars.current.scrollTop(
                        elementRect.top - scrollbarsRect.top - 20,
                    );
                }
            }, 100);
        },
        [scrollbars],
    );

    useEffect(() => {
        if (
            accountsPage ===
                getEntityPaneKey(EntitySettingsPages.SETTINGS, entity.id) &&
            activeAccountId &&
            openedAccounts.length === 0
        ) {
            const selectedAccount = baseAccounts.find(
                (a) => a.id === activeAccountId,
            );

            if (selectedAccount) {
                setOpenedAccounts([selectedAccount.id]);
                scrollToAccount(activeAccountId);
            }
        }
    }, [
        accountsPage,
        activeAccountId,
        baseAccounts,
        entity,
        scrollToAccount,
        openedAccounts,
    ]);

    const onOpen = useCallback((account: FinancialAccount) => {
        setOpenedAccounts((accounts) => [...accounts, account.id]);
    }, []);

    const onClose = useCallback((account: FinancialAccount) => {
        setActiveAccountId(undefined);
        setOpenedAccounts((accounts) =>
            accounts.filter((a) => a !== account.id),
        );
    }, []);

    const accounts: ExtendedFinancialAccount[] = useMemo(
        () =>
            baseAccounts
                .map((account) => ({
                    ...account,
                    accountNumber:
                        accountNumbers?.[account.id]?.accountNumber ??
                        undefined,
                }))
                .filter(
                    (account) =>
                        (!search ||
                            account.name
                                ?.toLowerCase()
                                .includes(search.toLowerCase())) ??
                        account.accountNumberMask
                            ?.toLowerCase()
                            .includes(search.toLowerCase()),
                ),
        [accountNumbers, baseAccounts, search],
    );

    if (!accounts) {
        return <Loader />;
    }

    if (!baseAccounts.length) {
        return (
            <section className="accounts-settings">
                <SectionHeader>
                    <h4>Accounts</h4>
                </SectionHeader>
                <EntityNoAccounts />
            </section>
        );
    }

    return (
        <section className="accounts-settings">
            <SectionHeader>
                <h4>Accounts</h4>
            </SectionHeader>
            <TablePageHeader search={search} onSearchChange={setSearch}>
                <AddAccount
                    btnSize="md"
                    btnText="Add account"
                    btnVariant="primary"
                    defaultEntity={entity}
                />
            </TablePageHeader>

            <main>
                {accounts.map((account) => (
                    <Collapsible
                        header={
                            <HStack
                                justify="between"
                                align="center"
                                data-account-id={account.id}
                            >
                                <AccountCard account={account} numberInName />
                                {isNumber(account.balance) ? (
                                    <span className="font-weight-medium">
                                        {currencyFormatter.format(
                                            account.balance,
                                        )}
                                    </span>
                                ) : null}
                            </HStack>
                        }
                        key={account.id}
                        open={openedAccounts.includes(account.id)}
                        onToggle={(opened) =>
                            opened ? onOpen(account) : onClose(account)
                        }
                        stacked
                    >
                        {(opened) => (
                            <AccountDetails
                                account={account}
                                isOpened={opened}
                            />
                        )}
                    </Collapsible>
                ))}

                {accounts.length === 0 && (
                    <EmptyState header="No accounts found" />
                )}
            </main>
        </section>
    );
};
